import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { PlusIcon } from '@heroicons/react/24/outline'
import {
  Badge,
  Button,
  DataTable,
  FilterChips,
  PageHeader,
  type ColumnDef,
  type FilterChipOption,
} from '@lipaflow/ui'
import { adminService, type Tenant } from '../services/admin'
import { usePageTitle } from '../lib/pageTitle'
import { sectorPath, useSector } from '../sector/sector'
import { CustomerPanel } from './customers/CustomerPanel'

type StatusFilter = 'all' | 'active' | 'inactive'
type OnboardingFilter = 'all' | 'pending' | 'stalled' | 'in_progress' | 'completed'
type View = 'all' | 'active' | 'inactive' | 'setting_up' | 'stuck'

// The URL is the contract: the admin dashboard and the header search link
// here with ?q=, ?status= and ?onboarding=. The sector is the workspace's.
function parseStatusFilter(value: string | null): StatusFilter {
  return value === 'active' || value === 'inactive' ? value : 'all'
}

function parseOnboardingFilter(value: string | null): OnboardingFilter {
  return value === 'pending' || value === 'stalled' || value === 'in_progress' || value === 'completed' ? value : 'all'
}

const PAGE_SIZE = 25

/**
 * Customers — every organisation using LipaFlow in the sector.
 *
 * One list with the views staff actually ask for (active, not active, still
 * setting up, stuck), one "Onboard a customer" action, and a panel per
 * customer that holds every other action. It used to be five filter
 * controls, five coloured stat boxes and six icon-only buttons per row.
 */
export default function TenantsPage() {
  usePageTitle('Customers')
  const sector = useSector()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [page, setPage] = useState(1)
  const [selected, setSelected] = useState<Tenant | null>(null)

  const searchQuery = searchParams.get('q') ?? ''
  const statusFilter = parseStatusFilter(searchParams.get('status'))
  const onboardingFilter = parseOnboardingFilter(searchParams.get('onboarding'))

  const setParams = (updates: Partial<Record<'q' | 'status' | 'onboarding', string>>) => {
    const next = new URLSearchParams(searchParams)
    for (const [key, raw] of Object.entries(updates)) {
      const value = raw?.trim() ?? ''
      if (!value || value === 'all') next.delete(key)
      else next.set(key, value)
    }
    setSearchParams(next, { replace: true })
  }

  const code = sector.code
  const { data: tenants, isLoading } = useQuery({ queryKey: ['tenants', code], queryFn: () => adminService.listTenants(code) })
  const { data: pending = [] } = useQuery({ queryKey: ['onboarding', code, 'pending'], queryFn: () => adminService.getPendingOnboardings(code) })
  const { data: stalled = [] } = useQuery({ queryKey: ['onboarding', code, 'stalled'], queryFn: () => adminService.getStalledOnboardings(code) })
  const { data: completed = [] } = useQuery({ queryKey: ['onboarding', code, 'completed'], queryFn: () => adminService.getCompletedOnboardings(code) })

  const pendingIds = new Set(pending.map((i) => i.tenant_id))
  const stalledIds = new Set(stalled.map((i) => i.tenant_id))
  const completedIds = new Set(completed.map((i) => i.tenant_id))
  const inProgressIds = new Set(pending.filter((i) => i.status.toLowerCase() === 'in_progress').map((i) => i.tenant_id))
  const onboardNew = () => navigate(sectorPath(code, 'onboarding/new'))

  const q = searchQuery.trim().toLowerCase()
  const filtered = (tenants ?? [])
    .filter((t) => {
      if (q && !t.name.toLowerCase().includes(q) && !t.short_name.toLowerCase().includes(q)) return false
      if (statusFilter === 'active' && !t.is_active) return false
      if (statusFilter === 'inactive' && t.is_active) return false
      if (onboardingFilter === 'pending' && !pendingIds.has(t.id)) return false
      if (onboardingFilter === 'stalled' && !stalledIds.has(t.id)) return false
      if (onboardingFilter === 'completed' && !completedIds.has(t.id)) return false
      if (onboardingFilter === 'in_progress' && !inProgressIds.has(t.id)) return false
      return true
    })
    .sort((a, b) => a.name.localeCompare(b.name))

  useEffect(() => setPage(1), [searchQuery, statusFilter, onboardingFilter, code])
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

  // One chip row over the two URL filters. Any other combination (e.g. from
  // a dashboard link) still filters correctly; it just shows as "All".
  const view: View =
    onboardingFilter === 'stalled' ? 'stuck'
      : onboardingFilter === 'pending' || onboardingFilter === 'in_progress' ? 'setting_up'
        : statusFilter === 'active' && onboardingFilter === 'all' ? 'active'
          : statusFilter === 'inactive' && onboardingFilter === 'all' ? 'inactive'
            : 'all'
  const all = tenants ?? []
  const views: FilterChipOption<View>[] = [
    { key: 'all', label: 'All customers', count: all.length },
    { key: 'active', label: 'Active', count: all.filter((t) => t.is_active).length },
    { key: 'inactive', label: 'Not active', count: all.filter((t) => !t.is_active).length },
    { key: 'setting_up', label: 'Setting up', count: pendingIds.size },
    { key: 'stuck', label: 'Stuck in setup', count: stalledIds.size, countNeedsAttention: true },
  ]
  const chooseView = (v: View) =>
    setParams({
      status: v === 'active' || v === 'inactive' ? v : 'all',
      onboarding: v === 'setting_up' ? 'pending' : v === 'stuck' ? 'stalled' : 'all',
    })

  const setupBadge = (t: Tenant) =>
    stalledIds.has(t.id) ? <Badge variant="warning" size="sm">Stuck in setup</Badge>
      : pendingIds.has(t.id) ? <Badge variant="info" size="sm">Setting up</Badge>
        : null

  const columns: ColumnDef<Tenant>[] = [
    {
      key: 'name',
      header: 'Customer',
      cell: (t) => (
        <div className="flex items-center gap-3">
          <span
            className="h-9 w-9 shrink-0 rounded-chip flex items-center justify-center overflow-hidden bg-surface-alt"
            aria-hidden
          >
            {t.logo_url ? (
              <img src={t.logo_url} alt="" className="h-full w-full object-cover" />
            ) : (
              <span className="font-outfit font-semibold text-accent">{t.short_name[0]?.toUpperCase()}</span>
            )}
          </span>
          <span className="min-w-0">
            <span className="block font-medium text-ink truncate">{t.name}</span>
            <span className="block text-caption text-ink-muted font-mono">{t.short_name}</span>
          </span>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      cell: (t) => (
        <span className="flex flex-wrap gap-1">
          <Badge variant={t.is_active ? 'success' : 'neutral'} size="sm">{t.is_active ? 'Active' : 'Not active'}</Badge>
          {setupBadge(t)}
        </span>
      ),
    },
    {
      key: 'joined',
      header: 'Joined',
      hideOnMobile: true,
      cell: (t) => (
        <span className="text-body-sm text-ink-muted">
          {new Date(t.created_at).toLocaleDateString('en-KE', { day: 'numeric', month: 'short', year: 'numeric' })}
        </span>
      ),
    },
  ]

  const filtering = Boolean(searchQuery || statusFilter !== 'all' || onboardingFilter !== 'all')

  return (
    <div className="space-y-6">
      <PageHeader
        title="Customers"
        subtitle={`Every organisation using LipaFlow for ${sector.name}. Open one to see its details, support it, or change its status.`}
        actions={
          <Button leftIcon={<PlusIcon className="h-4 w-4" />} onClick={onboardNew}>
            Onboard a customer
          </Button>
        }
      />

      <FilterChips options={views} value={view} onChange={chooseView} />

      <DataTable
        data={paged}
        columns={columns}
        getRowId={(t) => t.id}
        loading={isLoading}
        pagination={{ page, pageSize: PAGE_SIZE, total: filtered.length }}
        onPageChange={setPage}
        searchable
        searchPlaceholder="Search by name or short name"
        searchValue={searchQuery}
        onSearch={(value) => setParams({ q: value })}
        onRowClick={setSelected}
        activeRowId={selected?.id}
        emptyState={{
          title: filtering ? 'No customers match' : 'No customers yet',
          description: filtering ? 'Try another view or search.' : 'Onboard your first customer to get started.',
          action: filtering
            ? <Button variant="secondary" onClick={() => setSearchParams({}, { replace: true })}>Clear filters</Button>
            : <Button onClick={onboardNew}>Onboard a customer</Button>,
        }}
        mobileCardRenderer={(t) => (
          <button type="button" onClick={() => setSelected(t)} className="w-full text-left p-4 border-b border-hairline">
            <span className="flex items-center justify-between gap-2">
              <span className="font-medium text-ink truncate">{t.name}</span>
              <Badge variant={t.is_active ? 'success' : 'neutral'} size="sm">{t.is_active ? 'Active' : 'Not active'}</Badge>
            </span>
            <span className="block text-caption text-ink-muted font-mono">{t.short_name}</span>
          </button>
        )}
      />

      <CustomerPanel customer={selected} onClose={() => setSelected(null)} />
    </div>
  )
}
